import { NumberValidation, StringValidation } from '@amzn/awscat-portfolio-assessment-types';
import { Alert, Button, Checkbox, CheckboxProps, ExpandableSection, Form, FormField, Input,
    InputProps, NonCancelableCustomEvent, RadioGroup, RadioGroupProps, Select, SelectProps, SpaceBetween,
    Toggle, ToggleProps,
} from '@amzn/awsui-components-react';
import * as React from 'react';

import { constants } from '../../common/Localization';
import { numberOfDecimals } from '../../common/util';

type FieldType = 'number' | 'enum' | 'multiValueEnum' | 'string' | 'boolean' | 'object' | 'radioGroup' | 'password';

export type EnumItem = {
    value: string;
    text: string;
    uiDisabled?: boolean;
};

export interface MetadataItem<T> {
    identifier: keyof T;
    type?: FieldType;
    defaultValue?: any;
    label: string;
    description?: string;
    'enum'?: EnumItem[];
    selections?: EnumItem[];
    uiHidden?: boolean;
    uiDisabled?: boolean;
    validation?: NumberValidation | StringValidation;
    placeholder?: string;
    required?: boolean;
    properties?: DistributeMetadataItem<T>[];
    alertMsgs?: string[];
}

export type DistributeMetadataItem<T> = T extends any ? MetadataItem<T> : never;

export interface AWSUIFormBuilderProps<T> {
    item: T;
    metadata: DistributeMetadataItem<T>[];
    loading?: boolean;
    readonly?: boolean;
    formDeleteMessage?: JSX.Element;
    primaryAction?: string;
    onCancel?: () => void;
    // setting onUpdateWithStateChange will trigger a form state change and rerender the form components when a user updates a form item
    // use onUpdateWithStateChange if the form display should change depending on a user selection
    // use onUpdate if the form display is static
    onUpdateWithStateChange?: (item: T) => T;
    onUpdate?: (item: T, isFormValid?: boolean) => void; // pass the validation status out when the submit button is controlled by outer component
    onSubmit?: (item: T) => void;
    className?: string;
    hasActions?: boolean;
}

type AbstractObject<T> = {
    [P in keyof T]: T[P];
};

function AWSUIFormBuilder<T extends AbstractObject<T>>(props: AWSUIFormBuilderProps<T>): JSX.Element {
    const { item, metadata, loading, readonly, formDeleteMessage, primaryAction, onCancel, onUpdate, onUpdateWithStateChange, onSubmit, className } = props;
    const [ modifiedItem, setModifiedItem ] = React.useState<T>({...item});
    const [ validationMessages, setValidationMessages ] = React.useState<string[]>(Array(metadata.length).fill(''));

    React.useEffect(() => {
        setModifiedItem({...item});
    }, [item]);


    const isFormValid = () => {
        let missingValue = false;
        metadata.forEach((field => {
            const value = modifiedItem[field.identifier];
            if (field.required && (value == null || value === '')) {
                missingValue = true;
            }
        }));
        return !missingValue && validationMessages.every(message => !message);
    };

    React.useEffect(() => {
        if (onUpdateWithStateChange) {
            setModifiedItem(onUpdateWithStateChange(modifiedItem));
        }
        if (onUpdate) {
            onUpdate(modifiedItem, isFormValid());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modifiedItem]);

    function validateString(value: string | null, validation?: StringValidation): string {
        if (!validation || value == null) {
            return '';
        }
        const { minLen, maxLen } = validation;
        if (minLen != null && value.length < minLen) {
            return `Length must be longer than ${minLen} characters`;
        } else if (maxLen != null && value.length > maxLen) {
            return `Length must be shorter than ${maxLen} characters`;
        }
        return '';
    }

    function validateNumber(value: number | null, required: boolean, validation?: NumberValidation): string {
        if (!validation || (!required && value === null)) {
            return '';
        }
        if (value === null) {
            return constants.FORM_VALUE_MUST_BE_DEFINED;
        }
        const { min, max, decimalPlaces } = validation;
        if (decimalPlaces != null && numberOfDecimals(value) > decimalPlaces) {
            return `${constants.FORM_SHOULD_BE_NO_GREATER_THAN} ${decimalPlaces} ${constants.FORM_DECIMAL_PLACES}`;
        }
        if (min != null && value < min) {
            return `${constants.FORM_SHOULD_BE_NO_LESS_THAN} ${min}`;
        }
        if (max != null && value > max) {
            return `${constants.FORM_SHOULD_BE_NO_GREATER_THAN} ${max}`;
        }
        else {
            return '';
        }
    }

    function generateField(field: MetadataItem<T>, index: number): React.ReactNode {
        const { identifier, label, description, selections, validation, uiDisabled, uiHidden, placeholder, properties, required } = field;
        const enumValues = field.enum;
        const type = field.type ?? 'object';

        let input: JSX.Element = (<span></span>);

        // Input change handlers (they each update the property's state)
        const onTextChange = (event: NonCancelableCustomEvent<InputProps.ChangeDetail>) => {
            const value = event.detail.value;
            const errorMessage = validateString(value, validation as StringValidation);
            const modifiedValidationMessages = [...validationMessages];
            modifiedValidationMessages[index] = errorMessage;
            setValidationMessages(modifiedValidationMessages);
            setModifiedItem({
                ...modifiedItem,
                [identifier]: value,
            });
        };

        const onNumberChange = (event: NonCancelableCustomEvent<InputProps.ChangeDetail>) => {
            const value = event.detail.value ? parseFloat(event.detail.value) : null;
            const errorMessage = validateNumber(value, (required === undefined) ? false : required, validation as NumberValidation);
            const modifiedValidationMessages = [...validationMessages];
            modifiedValidationMessages[index] = errorMessage;
            setValidationMessages(modifiedValidationMessages);
            setModifiedItem({
                ...modifiedItem,
                [identifier]: value,
            });
        };

        const onSelectionChange = (event: NonCancelableCustomEvent<SelectProps.ChangeDetail>) => {
            setModifiedItem({
                ...modifiedItem,
                [identifier]: event.detail.selectedOption.value,
            });
        };

        const onRadioButtonChange = (event: NonCancelableCustomEvent<RadioGroupProps.ChangeDetail>) => {
            setModifiedItem({
                ...modifiedItem,
                [identifier]: event.detail.value,
            });
        };

        const onCheckedSelectionChange = (value: string, selectedItems: string[]) => (event: NonCancelableCustomEvent<CheckboxProps.ChangeDetail>): void => {
            const { checked } = event.detail;
            const index = selectedItems.findIndex(selection => selection === value);
            if (index > -1 && !checked) {
                selectedItems.splice(index, 1);
            }
            else if (index < 0 && checked) {
                selectedItems.push(value);
            }
            setModifiedItem({
                ...modifiedItem,
                [identifier]: selectedItems,

            });
        };

        const onBooleanChange = (event: NonCancelableCustomEvent<ToggleProps.ChangeDetail>) => {
            setModifiedItem({
                ...modifiedItem,
                [identifier]: event.detail.checked,
            });
        };

        // Type Guard functions
        const isStringType = (type: FieldType, value: any): value is string => {
            return type === 'string';
        };

        const isNumberType = (type: FieldType, value: any): value is number => {
            return type === 'number';
        };

        const isEnumType = (type: FieldType, value: any): value is string => {
            return type === 'enum';
        };

        const isMultiVarEnumType = (type: FieldType, value: any): value is string[] => {
            return type === 'multiValueEnum';
        };

        const isBooleanType = (type: FieldType, value: any): value is boolean => {
            return type === 'boolean';
        };

        const isRadioButtonType = (type: FieldType, value: any): value is string => {
            return type === 'radioGroup';
        };

        const isObjectType = (type: FieldType, value: any): value is AbstractObject<T> => {
            return type === 'object';
        };

        const isPasswordType = (type: FieldType, value: any): value is AbstractObject<T> => {
            return type === 'password';
        };

        if (isStringType(type, modifiedItem[identifier])) {
            input = (
                <Input
                    value={modifiedItem[identifier]}
                    onChange={onTextChange}
                    disabled={readonly || uiDisabled}
                    placeholder={placeholder}
                />
            );
        }
        else if (isNumberType(type, modifiedItem[identifier])) {
            input = (
                <Input
                    type='number'
                    inputMode='numeric'
                    value={modifiedItem[identifier]}
                    onChange={onNumberChange}
                    disabled={readonly || uiDisabled}
                    placeholder={placeholder}
                />
            );
        }
        else if (isEnumType(type, modifiedItem[identifier]) && enumValues) {
            const selectItems = enumValues.map(item => {
                return {
                    value: item.value,
                    label: item.text,
                    uiDisabled: item.uiDisabled
                };
            });
            
            input = (
                <Select
                    selectedOption={selectItems.find(option => option.value === modifiedItem[identifier]) || null}
                    options={selectItems}
                    onChange={onSelectionChange}
                    disabled={readonly || uiDisabled}
                    placeholder={placeholder}
                    key={modifiedItem[identifier]}
                />
            );
        }
        else if (isRadioButtonType(type, modifiedItem[identifier]) && enumValues) {
            const radioItems = enumValues.map(item => {
                return {
                    value: item.value,
                    label: item.text,
                };
            });
            
            input = (
                <RadioGroup
                    value={modifiedItem[identifier]}
                    onChange={onRadioButtonChange}
                    items={radioItems}
                    key={modifiedItem[identifier]}
                />
            );
        }
        else if (isMultiVarEnumType(type, modifiedItem[identifier]) && selections) {
            const isChecked = (value: string, property: string[]): boolean => {
                return property.includes(value);
            };

            input = (
                <span>
                    {selections.map(option => {
                        return (
                            <Checkbox
                                checked={isChecked(option.value, modifiedItem[identifier])}
                                onChange={onCheckedSelectionChange(option.value, modifiedItem[identifier])}
                                disabled={readonly || uiDisabled || option.uiDisabled}
                                key={option.text}
                            >
                                {option.text}
                            </Checkbox>
                        );
                    })}
                </span>
            );
        } 
        else if (isBooleanType(type, modifiedItem[identifier])) {
            input = (
                <Toggle
                    onChange={onBooleanChange}
                    checked={modifiedItem[identifier]}
                    description={description}
                    key={modifiedItem[identifier]}
                >
                    {label}
                </Toggle>
            );
        }
        else if (isPasswordType(type, modifiedItem[identifier])) {
            input = (
                <Input
                    value={modifiedItem[identifier]}
                    onChange={onTextChange}
                    disabled={readonly || uiDisabled}
                    type="password"
                />
            );
        }

        const getDisplayField = (): React.ReactNode => {
            if (uiHidden) {
                return null;
            }
            else if (isObjectType(type, modifiedItem[identifier]) && properties) {
                return (
                    <ExpandableSection
                        header={label}
                        key={label}
                    >
                        {properties.map((field, index) => generateField(field, index))}
                    </ExpandableSection>
                );
            }
            else if (isBooleanType(type, modifiedItem[identifier])) {
                return (
                    <FormField
                        errorText={validationMessages[index]}
                        key={index}
                    >
                        {input}
                    </FormField>
                );
            } 
            else {
                return (
                    <FormField
                        data-testid={identifier}
                        label={label}
                        description={description}
                        errorText={validationMessages[index]}
                        key={index}
                    >
                        <SpaceBetween size='m'>
                            {input}
                            {
                                (field.alertMsgs && field.alertMsgs.length > 0) &&
                                field.alertMsgs.map((msg: string, index: number) => {
                                    return (<Alert key={index} type='warning' dismissible={false}>{msg}</Alert>);
                                })
                            }
                        </SpaceBetween>
                    </FormField>
                );
            }
        };

        return getDisplayField();
    }

    const onCancelClick = () => {
        if (onCancel) {
            onCancel();
        }
    };

    const onSubmitClick = () => {
        if (onSubmit) {
            onSubmit(modifiedItem);
        }
    };

    return (
        <form onSubmit={e => e.preventDefault()} className={className}>
            <Form
                actions={props.hasActions ?? true ? 
                    (
                        <SpaceBetween direction="horizontal" size="xs">
                            {onCancel ? <Button variant="link" disabled={loading} onClick={onCancelClick}>Cancel</Button> : null}
                            {onSubmit ? (
                                <Button variant="primary" disabled={!isFormValid()} loading={loading} onClick={onSubmitClick} id='btn-formbuilder-submit'>
                                    {primaryAction ?? 'Submit'}
                                </Button>
                            )
                                : null}
                        </SpaceBetween>
                    ) : null }
                errorText={formDeleteMessage}
            >
                <SpaceBetween size='l'>
                    {metadata.map((field, index) => generateField(field, index))}
                </SpaceBetween>
            </Form>
        </form>
    );
}

export default AWSUIFormBuilder;
